import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import LazyImage from 'dibs-react-lazy-image';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { shopCreatorAtSeller } from '../sbMessages';
import { SbSharedRelatedSellerTitle } from './SbSharedRelatedSellerTitle';
import classNames from 'classnames';

import styles from './SbSharedRelatedSeller.scss';

import type { SbSharedRelatedSeller_seller$key } from './__generated__/SbSharedRelatedSeller_seller.graphql';
import type { SbSharedRelatedSeller_sellerStorefrontItems$key } from './__generated__/SbSharedRelatedSeller_sellerStorefrontItems.graphql';
import type { SbSharedRelatedSeller_creatorShopLink$key } from './__generated__/SbSharedRelatedSeller_creatorShopLink.graphql';
import type { SbSharedRelatedSeller_itemSearch$key } from './__generated__/SbSharedRelatedSeller_itemSearch.graphql';
import type { SbSharedRelatedSeller_user$key } from './__generated__/SbSharedRelatedSeller_user.graphql';

export type Props = {
    sellerStorefrontItems: SbSharedRelatedSeller_sellerStorefrontItems$key;
    seller: SbSharedRelatedSeller_seller$key;
    creatorShopLink: SbSharedRelatedSeller_creatorShopLink$key;
    itemSearch: SbSharedRelatedSeller_itemSearch$key;
    user: SbSharedRelatedSeller_user$key;
    isHidden?: boolean;
};

const LAZY_IMAGE_VERTICAL_OFFSET: number = 200;
const CREATOR_ITEM_IMAGE_SIZES: string = '(max-width: 568px) 33vw, 15vw';

export const SbSharedRelatedSeller: FC<Props> = ({
    sellerStorefrontItems: sellerStorefrontItemsRef,
    seller: sellerRef,
    creatorShopLink: creatorShopLinkRef,
    itemSearch: itemSearchRef,
    user: userRef,
    isHidden,
}) => {
    const sellerStorefrontItems = useFragment(
        graphql`
            fragment SbSharedRelatedSeller_sellerStorefrontItems on RelatedSellersStorefrontItems {
                itemsAvailable
                items {
                    item {
                        title
                        localizedPdpUrl
                        photos(limit: 1) {
                            smallPath
                        }
                    }
                }
            }
        `,
        sellerStorefrontItemsRef
    );
    const seller = useFragment(
        graphql`
            fragment SbSharedRelatedSeller_seller on Seller {
                ...SbSharedRelatedSellerTitle_seller
            }
        `,
        sellerRef
    );

    const creatorShopLink = useFragment(
        graphql`
            fragment SbSharedRelatedSeller_creatorShopLink on LinkData {
                ...SeoLink_linkData
            }
        `,
        creatorShopLinkRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRelatedSeller_itemSearch on ItemSearchQueryConnection {
                meta {
                    header
                }
            }
        `,
        itemSearchRef
    );

    const user = useFragment(
        graphql`
            fragment SbSharedRelatedSeller_user on User {
                ...SbSharedRelatedSellerTitle_user
            }
        `,
        userRef
    );

    const storefrontItems = (sellerStorefrontItems?.items || []).map(storefrontItem => {
        const item = storefrontItem?.item;
        return {
            item: {
                localizedPdpUrl: item?.localizedPdpUrl || '',
                photos: (item?.photos || []).map(photo => ({
                    smallPath: photo?.smallPath || '',
                })),
                title: item?.title || '',
            },
        };
    });

    return (
        <div
            className={classNames(styles.relatedSellerTile, {
                [styles.hideTile]: isHidden,
            })}
        >
            <SbSharedRelatedSellerTitle seller={seller} user={user} />
            <div className={styles.relatedSellerItems}>
                {storefrontItems.map(({ item }) => {
                    const { localizedPdpUrl, photos, title } = item;
                    const imageSrc = photos[0].smallPath;
                    return (
                        <Link
                            className={styles.relatedSellerItem}
                            href={localizedPdpUrl}
                            key={imageSrc}
                            onClick={() =>
                                trackEvent({
                                    category: 'promo interaction',
                                    action: 'creator seller module pdp click',
                                    label: 'none',
                                })
                            }
                        >
                            <LazyImage
                                placeholderClass={styles.placeholderClass}
                                offsetVertical={LAZY_IMAGE_VERTICAL_OFFSET}
                            >
                                <img
                                    className={styles.relatedSellerItemImage}
                                    src={imageSrc}
                                    srcSet={getSrcsetString(imageSrc)}
                                    sizes={CREATOR_ITEM_IMAGE_SIZES}
                                    alt={title}
                                />
                            </LazyImage>
                        </Link>
                    );
                })}
            </div>
            <div className={styles.shopAllTitle}>
                <SeoLink
                    linkData={creatorShopLink}
                    onClick={() =>
                        trackEvent({
                            category: 'promo interaction',
                            action: 'creator seller module seller click',
                            label: 'none',
                        })
                    }
                    dataTn="view-more-from-seller-button"
                >
                    {shopCreatorAtSeller({
                        creatorItemCount: sellerStorefrontItems?.itemsAvailable || 0,
                        creator: itemSearch?.meta?.header || '',
                    })}
                </SeoLink>
            </div>
        </div>
    );
};
